/**
 * TransparencyChip
 * CCH Care — 2026-05-22
 *
 * Small inline chip for documenter ≠ reviewer transparency surfaces.
 * Per Kevin's 2026-05-21 policy: same-person co-sign and outranked-
 * signer amendments are allowed, but flagged for visibility.
 *
 * Kinds:
 *  - self_reviewed     — supervisor co-signed sections they themselves
 *                        completed. Rendered on Supervisor Reviewed
 *                        banner; count = distinct sections affected.
 *  - will_self_review  — pre-cosign advisory for the Pending Co-Sign
 *                        queue: "you'd be co-signing your own work."
 *                        Same visual as self_reviewed, future tense.
 *  - supervisor_amended — amender's role outranks the original
 *                         section signer's role_at_signing. Rendered
 *                         on amendment history rows.
 */

import { COLORS } from '../../config/constants';

const VARIANTS = {
  self_reviewed: {
    label: 'Self-reviewed',
    icon: '👁',
    title: (count) =>
      `Documenter co-signed ${count} of their own section${count === 1 ? '' : 's'}`,
    fg: COLORS.warning,
    bg: COLORS.warningLight,
    border: COLORS.warning,
  },
  will_self_review: {
    label: 'Your work',
    icon: '👁',
    title: (count) =>
      `You documented ${count} section${count === 1 ? '' : 's'} on this record — co-signing will be a self-review`,
    fg: COLORS.warning,
    bg: COLORS.warningLight,
    border: COLORS.warning,
  },
  supervisor_amended: {
    label: 'Supervisor-amended',
    icon: '⚖️',
    title: () => 'Amended by a user whose role outranks the original signer',
    fg: COLORS.primary,
    bg: COLORS.primaryLight,
    border: COLORS.primaryAlpha,
  },
};

export default function TransparencyChip({ kind, count = null, style = {} }) {
  const v = VARIANTS[kind];
  if (!v) return null;

  return (
    <span
      title={v.title(count)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        fontWeight: 700,
        color: v.fg,
        background: v.bg,
        border: `1px solid ${v.border}`,
        padding: '2px 8px',
        borderRadius: 12,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <span aria-hidden style={{ fontSize: 10 }}>{v.icon}</span>
      <span>
        {v.label}
        {count != null && count > 1 ? ` · ${count}` : ''}
      </span>
    </span>
  );
}